import {
  type SearchToken,
  type VariantSearchDocument,
  variantSearchDocumentSchema,
} from "@commerce-os/contracts";
import { z } from "zod";

const hitsSchema = z.object({ hits: z.array(variantSearchDocumentSchema) });

export type SearchProducts = (
  token: SearchToken,
  query: { q: string; status: "active" | "archived" },
  signal?: AbortSignal,
) => Promise<VariantSearchDocument[]>;

/**
 * Searches Meilisearch directly with the tenant token (docs/specs/search.md): Meilisearch adds the tenant filter,
 * so a query cannot reach another tenant's products. The answer is validated like an API response.
 */
export function createProductSearch(fetchFn: typeof fetch): SearchProducts {
  return async (token, { q, status }, signal) => {
    const response = await fetchFn(new URL(`/indexes/${token.indexUid}/search`, token.host), {
      method: "POST",
      headers: { authorization: `Bearer ${token.token}`, "content-type": "application/json" },
      body: JSON.stringify({ q, filter: `status = ${status}`, limit: 50 }),
      ...(signal === undefined ? {} : { signal }),
    });
    if (!response.ok) throw new Error(`search failed: ${String(response.status)}`);
    return hitsSchema.parse(await response.json()).hits;
  };
}
